import api from '@/api';

/**
 * Composable for generating tests (Codility or English)
 * @param {Object} options - Options for the test generator
 * @param {Object} options.store - Vuex store for showing notifications
 * @param {Function} options.onGenerateTest - Callback function to call after test generation
 * @param {Function} options.onClose - Callback function to call after test generation (success or failure)
 * @returns {Object} - Object containing the generateTest function
 */
export function useTestGenerator({ store, onGenerateTest, onClose }) {
  /**
   * Generate a test link
   * @param {string} testType - Type of test to generate ('codility' or 'english')
   * @param parameters
   * @param {string|number} parameters.applicationId - ID of the application
   * @param {string|number} [parameters.testId] - ID of the test (required for Codility tests)
   * @param {string} [parameters.testName] - Name of the test (used for success message for Codility tests)
   * @returns {Promise<void>}
   */
  const generateTest = async (testType, parameters) => {
    try {
      if (testType === 'english') {
        await generateEnglishTest(parameters);
      } else if (testType === 'codility') {
        await generateCodilityTest(parameters);
      } else {
        console.error(`Unsupported test type: ${testType}`);
      }
    } catch (error) {
      console.error(`Error generating ${testType} test link:`, error);
    } finally {
      if (onClose) onClose();
    }
  };

  /**
   * Generate an English test link
   * @param {Object} params - Parameters for the test generation
   * @param {string|number} params.applicationId - ID of the application
   * @returns {Promise<void>}
   */
  const generateEnglishTest = async ({ applicationId }) => {
    const { data } = await api.applications.tests.english.generateLink({
      applicationId,
    });
    if (store) {
      store.commit('ui/addSnackbarMessage', {
        title: 'English test generated successfully!',
        type: 'success',
      });
    }
    if (onGenerateTest) onGenerateTest(data.english_assessment.test_link);
  };

  /**
   * Generate a Codility test link
   * @param {Object} params - Parameters for the test generation
   * @param {string|number} params.applicationId - ID of the application
   * @param {string|number} params.testId - ID of the test
   * @param {string} params.testName - Name of the test (used for success message)
   * @returns {Promise<void>}
   */
  const generateCodilityTest = async ({ applicationId, testId, testName }) => {
    const { data } = await api.applications.tests.codility.generateLink({
      testId,
      applicationId,
    });

    if (store && testName) {
      store.commit('ui/addSnackbarMessage', {
        title: `"${testName}" test successfully generated`,
        type: 'success',
      });
    }

    if (onGenerateTest) onGenerateTest(data.link);
  };

  return {
    generateTest,
  };
}
